import {useState, useEffect} from "react";

const THROTTLE_MS = 150;

// Re-renders at most once per THROTTLE_MS while scrolling (leading + trailing), so
// position checks still see where the scroll ended without rendering on every event.
const useScrollListener = (element, {enabled = true} = {}) => {
  const [scrolling, setScrolling] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const scrollContainer = element || window;

    let timer = null;
    let pending = false;
    let ended = false;

    const bump = () => setScrolling(prev => prev + 1);

    const tick = () => {
      if (pending) {
        pending = false;
        bump();
        timer = setTimeout(tick, THROTTLE_MS);
      } else {
        timer = null;
        if (ended) {
          ended = false;
          setScrolling(0);
        }
      }
    };

    const scrollStart = () => {
      ended = false;
      if (timer) {
        pending = true;
        return;
      }
      bump();
      timer = setTimeout(tick, THROTTLE_MS);
    };

    // Let a pending trailing update land before resetting
    const scrollEnd = () => {
      if (timer) ended = true;
      else setScrolling(0);
    };

    scrollContainer.addEventListener("scroll", scrollStart);
    scrollContainer.addEventListener("scrollend", scrollEnd);

    return () => {
      clearTimeout(timer);
      scrollContainer.removeEventListener("scroll", scrollStart);
      scrollContainer.removeEventListener("scrollend", scrollEnd);
    };
  }, [element, enabled]);

  return scrolling;
};

export default useScrollListener;
